import { currentLocale } from "../i18n";
import { markRequest } from "./activity";
import { isTokenExpired, tokenExpiresAt, tokenStore } from "./token";
import type { Notifications } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

/**
 * Raised for every non-2xx answer. Carries the parsed notifications so a caller
 * can show the server's own localised message instead of inventing one — the
 * API already answers in the user's language.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly notifications: Notifications | null;
  readonly body: unknown;

  constructor(status: number, notifications: Notifications | null, body: unknown) {
    super(firstMessage(notifications) ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.notifications = notifications;
    this.body = body;
  }

  /** 403 + policy_confirmation_needed: the privacy policy has to be accepted. */
  get needsPrivacyPolicy(): boolean {
    return (
      this.status === 403 &&
      typeof this.body === "object" &&
      this.body !== null &&
      (this.body as { policy_confirmation_needed?: boolean }).policy_confirmation_needed === true
    );
  }
}

function firstMessage(notifications: Notifications | null): string | undefined {
  return notifications?.messages?.[0]?.notification_value;
}

/**
 * Events the app reacts to globally, so individual screens do not each have to
 * handle an expired session.
 */
type SessionEvent = "unauthorized" | "privacy-policy-required";
type SessionListener = (event: SessionEvent, error: ApiError) => void;

const listeners = new Set<SessionListener>();

export function onSessionEvent(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(event: SessionEvent, error: ApiError): void {
  listeners.forEach((listener) => listener(event, error));
}

/**
 * Appends the filter parameters the read endpoints understand. Empty values are
 * dropped rather than sent as "": the controllers test with `present?`, so an
 * empty string would be ignored anyway, but leaving it out keeps the query key
 * of a cached request stable.
 */
export function withQuery(
  path: string,
  params: Record<string, string | number | null | undefined>,
): string {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    const asString = String(value).trim();
    if (asString) search.set(key, asString);
  });

  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skips the Authorization header — used by login and the public endpoints. */
  anonymous?: boolean;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await request(path, options);
  const payload = await parseBody(response);

  if (!response.ok) {
    throw toError(response, payload);
  }

  return payload as T;
}

/**
 * Same as apiFetch but hands back the raw Response as well. Login needs it: the
 * JWT arrives in the Authorization *response* header, not in the body.
 */
export async function apiFetchWithResponse<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; response: Response }> {
  const response = await request(path, options);
  const payload = await parseBody(response);

  if (!response.ok) {
    throw toError(response, payload);
  }

  return { data: payload as T, response };
}

async function request(path: string, options: RequestOptions): Promise<Response> {
  const { body, anonymous, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  finalHeaders.set("Accept", "application/json");
  // ApplicationController#set_language falls back to Accept-Language for anyone
  // without a stored preference — an anonymous caller states it per request,
  // because the API has no session to remember it in.
  finalHeaders.set("Accept-Language", currentLocale());

  if (body !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
  }

  if (!anonymous) {
    const token = tokenStore.get();
    if (isTokenExpired(token)) {
      // Sending it would only buy a 401. Dropping it here means the request
      // arrives unauthenticated, which the server answers the same way it
      // answers every other dead session — one code path, not two.
      tokenStore.clear();
    } else if (token) {
      finalHeaders.set("Authorization", token);
    }
  }

  // Reset the idle clock on the request, not on the answer: that is the moment
  // the server records as well.
  markRequest();

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // The server renews the token while a session is in use and sends the new one
  // back in the Authorization header (ApplicationController#refresh_jwt).
  // Without picking it up here, working steadily for twenty minutes ended in a
  // 401 in the middle of an action. Login is excluded: it is `anonymous` and
  // stores its own token, after clearing the cache of the previous session.
  if (!anonymous) {
    adoptRenewedToken(response.headers.get("Authorization"));
  }

  return response;
}

/**
 * Takes over a token the server renewed — but only one that is actually an
 * improvement on what we hold.
 *
 * The check is not paranoia about the server. A response carrying the renewed
 * token can end up in the browser's HTTP cache, and once it is there the
 * browser serves that stored copy — headers and all — every time the request
 * revalidates into a 304. Without this guard the application read a token that
 * had expired the evening before out of such a cached answer, wrote it over the
 * one it had just been given at login, and was logged out on the next click.
 * The server no longer lets those responses be stored (ApplicationController
 * marks them no-store), but a cache that was poisoned before that fix lives on
 * in the browser, so the client refuses a step backwards on its own.
 */
function adoptRenewedToken(renewed: string | null): void {
  if (!renewed) return;

  const current = tokenStore.get();
  if (renewed === current) return;

  const renewedExpiry = tokenExpiresAt(renewed);
  if (renewedExpiry === null || renewedExpiry <= Date.now()) return;

  const currentExpiry = tokenExpiresAt(current);
  if (currentExpiry !== null && renewedExpiry <= currentExpiry) return;

  tokenStore.set(renewed);
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // The API answers JSON everywhere; anything else means a proxy or an
    // unhandled exception page, and the raw text is the most useful thing left.
    return { raw: text };
  }
}

function toError(response: Response, payload: unknown): ApiError {
  const notifications =
    typeof payload === "object" && payload !== null
      ? ((payload as { notifications?: Notifications }).notifications ?? null)
      : null;

  const error = new ApiError(response.status, notifications, payload);

  if (error.needsPrivacyPolicy) {
    emit("privacy-policy-required", error);
  } else if (response.status === 401) {
    // Either the idle timeout fired (the server rotated the jti, so the token is
    // dead) or there never was a valid one. Both mean: back to the login screen.
    tokenStore.clear();
    emit("unauthorized", error);
  }

  return error;
}
